import { FactoryProvider } from '@angular/core';
import { CASE_INVESTIGATION_TRACKER_CLIENT } from '../../features/case-investigation-tracker/data-access/case-investigation-tracker-client.token';
import { CaseInvestigationTrackerMockService } from '../../features/case-investigation-tracker/services/case-investigation-tracker-mock.service';
import { CaseInvestigationTrackerService } from '../../features/case-investigation-tracker/services/case-investigation-tracker.service';
import { API_STATE_CLIENT } from '../../features/api-state/data-access/api-state-client.token';
import { ApiStateMockService } from '../../features/api-state/data-access/api-state-mock.service';
import { ApiStateService } from '../../features/api-state/data-access/api-state.service';
import { DashboardDataServiceImpl } from '../../features/dashboard/services/dashboard-data.service';
import { DASHBOARD_DATA } from '../../features/dashboard/services/dashboard-data.token';
import { DashboardMockService } from '../../features/dashboard/services/dashboard-mock.service';
import { FeatureComparisonMockService } from '../../features/feature-comparison/services/feature-comparison-mock.service';
import { FeatureComparisonService } from '../../features/feature-comparison/services/feature-comparison.service';
import { EmployeeVarianceManagementMockService } from '../../features/variance-tracker/components/employee-variance-management/services/employee-variance-management-mock.service';
import { EmployeeVarianceManagerService } from '../../features/variance-tracker/components/employee-variance-management/services/employee-variance-manager.service';
import { EmailAnalyticsMockService } from '../services/email-analytics-mock.service';
import { EmailAnalyticsService } from '../services/email-analytics.service';
import { EmailPreviewMockService } from '../services/email-preview-mock.service';
import { EmailPreviewService } from '../services/email-preview.service';
import { EmailSendMockService } from '../services/email-send-mock.service';
import { EmailSendService } from '../services/email-send.service';
import { TimelineEventsMockService } from '../services/timeline-events-mock.service';
import { TimelineEventsService } from '../services/timeline-events.service';
import { FavoritesMockService } from '../../shared/components/filter-bar/services/favorites-mock.service';
import { FavoritesService } from '../../shared/components/filter-bar/services/favorites.service';
import { EMAIL_ANALYTICS_CLIENT } from '../tokens/email-analytics-client.token';
import { EMAIL_PREVIEW_CLIENT } from '../tokens/email-preview-client.token';
import { EMAIL_SEND_CLIENT } from '../tokens/email-send-client.token';
import { EMPLOYEE_VARIANCE_MANAGER_CLIENT } from '../tokens/employee-variance-manager.token';
import { FEATURE_COMPARISON_CLIENT } from '../tokens/feature-comparison-client.token';
import { EMAIL_RESPONSE_CLIENT } from '../tokens/email-response-client.token';
import { EMAIL_RESOLUTION_RESPONSES_CLIENT } from '../tokens/email-resolution-responses-client.token';
import { FAVORITES_CLIENT } from '../tokens/favorites-client.token';
import { EmailResponseMockService } from '../services/email-response-mock.service';
import { EmailResponseService } from '../services/email-response.service';
import { EmailResolutionResponsesMockService } from '../services/email-resolution-responses-mock.service';
import { EmailResolutionResponsesService } from '../services/email-resolution-responses.service';
import { TIMELINE_EVENTS_CLIENT } from '../tokens/timeline-events-client.token';
import { USE_MOCKED_DATA } from '../tokens/use-mocked-data.token';
import { INCIDENT_TRANSACTIONS_CLIENT } from '../tokens/incident-transactions-client.token';
import { IncidentTransactionsMockService } from '../../features/variance-tracker/components/variance-supporting-data/services/incident-transactions-mock.service';
import { IncidentTransactionsService } from '../../features/variance-tracker/components/variance-supporting-data/services/incident-transactions.service';
import { VARIANCE_INCIDENT_DETAILS_CLIENT } from '../tokens/variance-incident-details-client.token';
import { VarianceIncidentDetailsMockService } from '../../features/variance-tracker/components/variance-supporting-data/services/variance-incident-details-mock.service';
import { VarianceIncidentDetailsService } from '../../features/variance-tracker/components/variance-supporting-data/services/variance-incident-details.service';
import { INCIDENT_STATUS_CLIENT } from '../tokens/incident-status-client.token';
import { IncidentStatusMockService } from '../../features/variance-tracker/components/variance-supporting-data/services/incident-status-mock.service';
import { IncidentStatusService } from '../../features/variance-tracker/components/variance-supporting-data/services/incident-status.service';
import { FILTERS_CLIENT } from '../tokens/filters-client.token';
import { FiltersMockService } from '../../features/dashboard/services/filters-mock.service';
import { FiltersService } from '../../features/dashboard/services/filters.service';
import { CASE_INVESTIGATION_CLIENT } from '../tokens/case-investigation-client.token';
import { CaseInvestigationMockService } from '../../features/variance-tracker/case-investigation/services/case-investigation-mock.service';

/**
 * Factory class for creating providers that switch between mock and real services
 * based on the USE_MOCKED_DATA flag.
 */
export class MockDataProviders {
  /**
   * Creates a provider for DASHBOARD_DATA that switches between mock and real implementations.
   */
  static provideDashboardData(): FactoryProvider {
    return {
      provide: DASHBOARD_DATA,
      useFactory: (
        useMocked: boolean,
        mockService: DashboardMockService,
        realService: DashboardDataServiceImpl
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, DashboardMockService, DashboardDataServiceImpl],
    };
  }

  /**
   * Creates a provider for API_STATE_CLIENT that switches between mock and real implementations.
   */
  static provideApiStateClient(): FactoryProvider {
    return {
      provide: API_STATE_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: ApiStateMockService,
        realService: ApiStateService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, ApiStateMockService, ApiStateService],
    };
  }

  /**
   * Creates a provider for FAVORITES_CLIENT that switches between mock and real implementations.
   */
  static provideFavoritesClient(): FactoryProvider {
    return {
      provide: FAVORITES_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: FavoritesMockService,
        realService: FavoritesService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, FavoritesMockService, FavoritesService],
    };
  }

  /**
   * Creates a provider for EMPLOYEE_VARIANCE_MANAGER_CLIENT that switches between mock and real implementations.
   */
  static provideEmployeeVarianceManagerClient(): FactoryProvider {
    return {
      provide: EMPLOYEE_VARIANCE_MANAGER_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmployeeVarianceManagementMockService,
        realService: EmployeeVarianceManagerService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, EmployeeVarianceManagementMockService, EmployeeVarianceManagerService],
    };
  }

  /**
   * Creates a provider for FEATURE_COMPARISON_CLIENT that switches between mock and real implementations.
   * Real implementation uses FAVORITES_CLIENT for comparison and filter favorites.
   */
  static provideFeatureComparisonClient(): FactoryProvider {
    return {
      provide: FEATURE_COMPARISON_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: FeatureComparisonMockService,
        realService: FeatureComparisonService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, FeatureComparisonMockService, FeatureComparisonService],
    };
  }

  static provideTimelineEventsClient(): FactoryProvider {
    return {
      provide: TIMELINE_EVENTS_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: TimelineEventsMockService,
        realService: TimelineEventsService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, TimelineEventsMockService, TimelineEventsService],
    };
  }

  static provideEmailPreviewClient(): FactoryProvider {
    return {
      provide: EMAIL_PREVIEW_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmailPreviewMockService,
        realService: EmailPreviewService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, EmailPreviewMockService, EmailPreviewService],
    };
  }

  static provideEmailAnalyticsClient(): FactoryProvider {
    return {
      provide: EMAIL_ANALYTICS_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmailAnalyticsMockService,
        realService: EmailAnalyticsService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, EmailAnalyticsMockService, EmailAnalyticsService],
    };
  }

  static provideEmailSendClient(): FactoryProvider {
    return {
      provide: EMAIL_SEND_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmailSendMockService,
        realService: EmailSendService
      ) => (useMocked ? mockService : realService),
      deps: [USE_MOCKED_DATA, EmailSendMockService, EmailSendService],
    };
  }

  static provideEmailResponseClient(): FactoryProvider {
    return {
      provide: EMAIL_RESPONSE_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmailResponseMockService,
        realService: EmailResponseService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, EmailResponseMockService, EmailResponseService],
    };
  }

  static provideEmailResolutionResponsesClient(): FactoryProvider {
    return {
      provide: EMAIL_RESOLUTION_RESPONSES_CLIENT,
      useFactory: (
        useMocked: boolean,
        mockService: EmailResolutionResponsesMockService,
        realService: EmailResolutionResponsesService
      ) => {
        return useMocked ? mockService : realService;
      },
      deps: [USE_MOCKED_DATA, EmailResolutionResponsesMockService, EmailResolutionResponsesService],
    };
  }

  static provideCaseInvestigationTrackerClient(): FactoryProvider {
    return {
      provide: CASE_INVESTIGATION_TRACKER_CLIENT,
      useFactory: (
        useMocked: boolean,
        mock: CaseInvestigationTrackerMockService,
        real: CaseInvestigationTrackerService
      ) => (useMocked ? mock : real),
      deps: [USE_MOCKED_DATA, CaseInvestigationTrackerMockService, CaseInvestigationTrackerService],
    };
  }

  /**
   * Creates a provider for INCIDENT_TRANSACTIONS_CLIENT that switches between mock and real implementations.
   */
  static provideIncidentTransactionsClient(): FactoryProvider {
    return {
      provide: INCIDENT_TRANSACTIONS_CLIENT,
      useFactory: (
        useMocked: boolean,
        mock: IncidentTransactionsMockService,
        real: IncidentTransactionsService
      ) => (useMocked ? mock : real),
      deps: [USE_MOCKED_DATA, IncidentTransactionsMockService, IncidentTransactionsService],
    };
  }

  static provideVarianceIncidentDetailsClient(): FactoryProvider {
    return {
      provide: VARIANCE_INCIDENT_DETAILS_CLIENT,
      useFactory: (
        useMocked: boolean,
        mock: VarianceIncidentDetailsMockService,
        real: VarianceIncidentDetailsService,
      ) => (useMocked ? mock : real),
      deps: [USE_MOCKED_DATA, VarianceIncidentDetailsMockService, VarianceIncidentDetailsService],
    };
  }

  static provideIncidentStatusClient(): FactoryProvider {
    return {
      provide: INCIDENT_STATUS_CLIENT,
      useFactory: (useMocked: boolean, mock: IncidentStatusMockService, real: IncidentStatusService) =>
        useMocked ? mock : real,
      deps: [USE_MOCKED_DATA, IncidentStatusMockService, IncidentStatusService],
    };
  }

  static provideFiltersClient(): FactoryProvider {
    return {
      provide: FILTERS_CLIENT,
      useFactory: (useMocked: boolean, mock: FiltersMockService, real: FiltersService) =>
        useMocked ? mock : real,
      deps: [USE_MOCKED_DATA, FiltersMockService, FiltersService],
    };
  }

  static provideCaseInvestigationClient(): FactoryProvider {
    return {
      provide: CASE_INVESTIGATION_CLIENT,
      useFactory: (
        useMocked: boolean,
        mock: CaseInvestigationMockService,
        real: EmployeeVarianceManagerService
      ) => (useMocked ? mock : real),
      deps: [USE_MOCKED_DATA, CaseInvestigationMockService, EmployeeVarianceManagerService],
    };
  }
}
